/**
 * @file     usePanelImpression.ts
 * @purpose  Fire an analytics event exactly once when a panel scrolls into
 *           view. Tiny wrapper around IntersectionObserver + useAnalytics
 *           so every data panel can declare its impression with one line.
 */
'use client';

import { useEffect, useRef } from 'react';
import { useAnalytics } from './useAnalytics';
import { reltaAnalytics } from '@/services/analytics/relta/client';

// GA4 event name → canonical Relta typed event name. Adding a panel: add a
// row here and the impression fires on both GA4 + Relta with no caller
// changes.
const RELTA_MIRROR: Record<string, string> = {
  property_my_read_viewed: 'panel.my_read.view',
  property_market_story_viewed: 'panel.market_story.view',
  property_supply_outlook_viewed: 'panel.supply_outlook.view',
  property_area_mix_viewed: 'panel.area_mix.view',
  property_demographics_viewed: 'panel.demographics.view',
  property_listing_history_viewed: 'panel.listing_history.view',
};

interface Options {
  threshold?: number;
  /** Extra metadata to attach to the event. */
  metadata?: Record<string, unknown>;
}

export function usePanelImpression(
  ref: React.RefObject<HTMLElement | null>,
  eventName: string,
  opts: Options = {},
): void {
  const { trackEvent } = useAnalytics();
  const fired = useRef(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const el = ref.current;
    if (!el || fired.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !fired.current) {
            fired.current = true;
            trackEvent({
              eventName,
              eventType: 'section',
              category: 'property_detail_panel',
              label: eventName,
              metadata: opts.metadata,
            });
            const reltaName = RELTA_MIRROR[eventName];
            if (reltaName) {
              reltaAnalytics.track(reltaName, opts.metadata ?? {});
            }
            obs.disconnect();
            return;
          }
        }
      },
      { threshold: opts.threshold ?? 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventName]);
}
